import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Users,
  Target,
  Clock,
  CheckCircle,
  Star,
  ArrowRight,
  FileText,
  HelpCircle,
  Building,
  Briefcase,
  BarChart,
  UserCheck,
  HardHat,
  Shield,
  TrendingUp,
  Award,
  Calculator,
} from "lucide-react";
import HomeBanner from "./components/HomeBanner";

const Employers = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <main>
        {/* Banner Section */}
        <HomeBanner />
        
        {/* Hero section */}
        <section className="relative text-white py-32">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/employer.webp"
              alt={t('employers.hero.imageAlt')}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary-dark/70 backdrop-blur-sm"></div>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('employers.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-white font-bold mb-8">
              {t('employers.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/post-job"
                className="bg-accent text-white px-8 py-3 rounded-lg hover:bg-accent-dark transition-colors shadow-lg"
              >
                {t('employers.hero.postJobButton')}
              </Link>
              <Link
                to="/contact"
                className="bg-white/90 text-primary px-8 py-3 rounded-lg hover:bg-white transition-colors shadow-lg backdrop-blur-sm"
              >
                {t('employers.hero.getStartedButton')}
              </Link>
              <Link
                to="/services"
                className="bg-primary-dark/80 text-white px-8 py-3 rounded-lg hover:bg-primary-dark transition-colors shadow-lg backdrop-blur-sm"
              >
                {t('employers.hero.exploreServicesButton')}
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative -mt-16 mb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { number: "95%", label: t("employers.stats.clientSatisfaction"), icon: Star },
                { number: "10k+", label: t("employers.stats.successfulPlacements"), icon: UserCheck },
                { number: "48hrs", label: t("employers.stats.averageResponseTime"), icon: Clock },
                { number: "20+", label: t("employers.stats.yearsOfExperience"), icon: Award },
              ].map((stat, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg p-6 transform hover:-translate-y-1 transition-all duration-300 border-l-4 border-accent"
                >
                  <div className="flex items-center justify-between mb-4">
                    <stat.icon className="w-8 h-8 text-primary" />
                    <span className="text-3xl font-bold text-gray-900">{stat.number}</span>
                  </div>
                  <p className="text-gray-600 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Welcome Message Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="prose prose-lg mx-auto">
              <p className="text-lg text-gray-600 mb-6">
                {t('employers.welcome.paragraph1')}
              </p>
              <p className="text-lg text-gray-600 mb-6">
                {t('employers.welcome.paragraph2')}
              </p>
              <p className="text-lg text-gray-600">
                {t('employers.welcome.paragraph3')}
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
                {t('employers.whyChooseUs.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('employers.whyChooseUs.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-primary group">
                <Target className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors" />
                <h3 className="text-xl font-semibold mb-4">{t('employers.whyChooseUs.tailoredSolutions.title')}</h3>
                <p className="text-gray-600">
                  {t('employers.whyChooseUs.tailoredSolutions.description')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-primary group">
                <Building className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors" />
                <h3 className="text-xl font-semibold mb-4">{t('employers.whyChooseUs.expertise.title')}</h3>
                <p className="text-gray-600">
                  {t('employers.whyChooseUs.expertise.description')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-primary group">
                <CheckCircle className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors" />
                <h3 className="text-xl font-semibold mb-4">{t('employers.whyChooseUs.commitment.title')}</h3>
                <p className="text-gray-600">
                  {t('employers.whyChooseUs.commitment.description')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-primary group">
                <HelpCircle className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors" />
                <h3 className="text-xl font-semibold mb-4">{t('employers.whyChooseUs.customerService.title')}</h3>
                <p className="text-gray-600">
                  {t('employers.whyChooseUs.customerService.description')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-primary group">
                <Star className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors" />
                <h3 className="text-xl font-semibold mb-4">{t('employers.whyChooseUs.trackRecord.title')}</h3>
                <p className="text-gray-600">
                  {t('employers.whyChooseUs.trackRecord.description')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-b-4 border-primary group">
                <Users className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors" />
                <h3 className="text-xl font-semibold mb-4">{t('employers.whyChooseUs.partnership.title')}</h3>
                <p className="text-gray-600">
                  {t('employers.whyChooseUs.partnership.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="mb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
                {t('employers.services.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('employers.services.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: t('employers.services.itTechnology'),
                  description: t('employers.services.itTechnologyDescription'),
                  color: 'orange',
                },
                {
                  icon: HardHat,
                  title: t('employers.services.construction'),
                  description: t('employers.services.constructionDescription'),
                  color: 'orange',
                },
                {
                  icon: Calculator,
                  title: t('employers.services.finance'),
                  description: t('employers.services.financeDescription'),
                  color: 'orange',
                },
                {
                  icon: Briefcase,
                  title: t('employers.services.administrative'),
                  description: t('employers.services.administrativeDescription'),
                  color: 'orange',
                },
                {
                  icon: TrendingUp,
                  title: t('employers.services.executiveSearch'),
                  description: t('employers.services.executiveSearchDescription'),
                  color: 'orange',
                },
                {
                  icon: Users,
                  title: t('employers.services.staffAugmentation'),
                  description: t('employers.services.staffAugmentationDescription'),
                  color: 'orange',
                },
                {
                  icon: Target,
                  title: t('employers.services.specializedRoles'),
                  description: t('employers.services.specializedRolesDescription'),
                  color: 'orange',
                },
              ].map((service, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group border-b-4 border-orange-500`}
                >
                  <div className="p-8">
                    <div className={`inline-flex p-3 rounded-lg bg-orange-100 text-green-500 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <Link
                      to="/services"
                      className={`inline-flex items-center text-green-500 hover:text-green-700 font-medium`}
                    >
                      {t('common.learnMore')} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="bg-primary-light py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">
                {t('employers.testimonials.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('employers.testimonials.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  quote: t('employers.testimonials.quote1'),
                  author: t('employers.testimonials.author1'),
                  position: t('employers.testimonials.position1'),
                },
                {
                  quote: t('employers.testimonials.quote2'),
                  author: t('employers.testimonials.author2'),
                  position: t('employers.testimonials.position2'),
                },
                {
                  quote: t('employers.testimonials.quote3'),
                  author: t('employers.testimonials.author3'),
                  position: t('employers.testimonials.position3'),
                },
              ].map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-orange-500"
                >
                  <div className="flex mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-600 mb-6">
                    "{testimonial.quote}"
                  </blockquote>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-gray-600">{testimonial.position}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-primary text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">{t('employers.cta.title')}</h2>
            <p className="text-xl mb-8">
              {t('employers.cta.description')}
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="/contact"
                className="bg-white text-primary px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('employers.cta.contactButton')}
              </Link>
              <Link
                to="/services"
                className="bg-accent text-white px-8 py-3 rounded-lg hover:bg-accent-dark transition-colors"
              >
                {t('employers.cta.servicesButton')}
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Employers;
